import type { tFeatureFlags } from './FeatureFlags.types';
import { FEATURE_FLAG_KEYS } from './FeatureFlags.constants';

/**
 * Why a flags resolution failed. `network` covers everything that never
 * produced a parseable body (transport error, timeout, non-2xx thrown by
 * axios); the other two come from the strict parse below.
 */
export type tFeatureFlagsParseReason = 'invalid_envelope' | 'backend_error';
export type tFeatureFlagsFailureReason = tFeatureFlagsParseReason | 'network';
export type tFeatureFlagsFailureCause = tFeatureFlagsParseReason
  | 'auth_degraded_401' | 'unauthorized_401' | 'http_4xx' | 'http_5xx' | 'timeout' | 'network';

/** Thrown by `parseFeatureFlagsPayload` when the payload is not flags. */
export class FeatureFlagsParseError extends Error {
  readonly reason: tFeatureFlagsParseReason;

  constructor(reason: tFeatureFlagsParseReason, message: string) {
    super(message);
    this.name = 'FeatureFlagsParseError';
    this.reason = reason;
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * RET-1922 — strict parse of the flags envelope.
 *
 * The BFF returns the flags either WRAPPED (`{ code, success, data, ... }` —
 * `tFeatureFlagsResponse`) or FLAT (`{ 'leads_smart-lead-bot': true, ... }`)
 * depending on the deployment. The previous `response?.data ?? response`
 * fallback accepted BOTH shapes and also everything else: an error rendered
 * with HTTP 200 (`{ success: false, message }`) spread into zero flag keys and
 * resolved every flag to its default, which for `leads_alert-center` meant the
 * legacy experience for the whole session (the D11 snapshot is
 * first-commit-wins) with no error anywhere. That silent OFF was measured at
 * ~1 in 3 page loads in flag-on workspaces. RET-1950 flipped that default, so
 * the same silent failure now points the other way — a workspace explicitly
 * turned OFF would silently get the center. Either direction is a lie about
 * what the backend said, which is why the parse stays strict.
 *
 * So: an unexpected shape THROWS. The caller treats the throw exactly like a
 * dead network — retry, and only then fall back to defaults, loudly.
 *
 * `{ data: {} }` stays a SUCCESS: an empty flags map is the contractual answer
 * for "this workspace has no flags set", not a failure.
 */
export function parseFeatureFlagsPayload(
  response: unknown,
): Partial<tFeatureFlags> {
  if (!isPlainObject(response)) {
    throw new FeatureFlagsParseError(
      'invalid_envelope',
      `Feature flags response is not an object (got ${typeof response})`,
    );
  }

  // A backend that answers `success: false` has told us it failed, whatever
  // the HTTP status says. Never merge its body.
  if (response.success === false) {
    throw new FeatureFlagsParseError(
      'backend_error',
      'Feature flags response reported success: false',
    );
  }

  if ('data' in response) {
    if (!isPlainObject(response.data)) {
      throw new FeatureFlagsParseError(
        'invalid_envelope',
        'Feature flags response carries a non-object `data`',
      );
    }
    return response.data as Partial<tFeatureFlags>;
  }

  const looksFlat = FEATURE_FLAG_KEYS.some((key) =>
    Object.prototype.hasOwnProperty.call(response, key),
  );
  if (!looksFlat) {
    throw new FeatureFlagsParseError(
      'invalid_envelope',
      'Feature flags response has no `data` and no known flag key',
    );
  }

  return response as Partial<tFeatureFlags>;
}

/** Maps whatever the last failed attempt threw onto the telemetry vocabulary. */
export function resolveFeatureFlagsFailureReason(
  error: unknown,
): tFeatureFlagsFailureReason {
  return error instanceof FeatureFlagsParseError ? error.reason : 'network';
}

export const getFeatureFlagsHttpStatus = (error: unknown): number | undefined => {
  if (!isPlainObject(error) || !isPlainObject(error.response)) return undefined;
  return typeof error.response.status === 'number' ? error.response.status : undefined;
};

/** Adds diagnostic detail without changing the existing reason vocabulary. */
export const resolveFeatureFlagsFailureCause = (error: unknown): tFeatureFlagsFailureCause => {
  if (error instanceof FeatureFlagsParseError) return error.reason;
  const status = getFeatureFlagsHttpStatus(error);
  if (status === 401) {
    return isPlainObject(error) && isPlainObject(error.config) && error.config.authDegraded === true
      ? 'auth_degraded_401' : 'unauthorized_401';
  }
  if (status !== undefined && status >= 500 && status < 600) return 'http_5xx';
  if (status !== undefined && status >= 400 && status < 500) return 'http_4xx';
  if (isPlainObject(error) && ['ECONNABORTED', 'ETIMEDOUT'].includes(String(error.code))) return 'timeout';
  return 'network';
};
